import React, { useEffect, useState, useCallback } from 'react';
import { financeiroAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, TrendingDown, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate, useNavigate } from 'react-router-dom';
import ExpenseFormModal from './ExpenseFormModal';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';

export default function FinanceiroDespesas() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    data_inicio: '',
    data_fim: '',
    categoria_id: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.data_inicio) params.data_inicio = filters.data_inicio;
      if (filters.data_fim) params.data_fim = filters.data_fim;
      if (filters.categoria_id) params.categoria_id = filters.categoria_id;
      const [e, c] = await Promise.all([
        financeiroAPI.listExpenses(params),
        financeiroAPI.listCategories('despesa'),
      ]);
      setList(e.data || []);
      setCategories(c.data || []);
    } catch (err) {
      toast.error('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { if (financeiroAtivo) load(); }, [financeiroAtivo, load]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Excluir esta despesa?')) return;
    try {
      await financeiroAPI.deleteExpense(id);
      toast.success('Despesa excluída');
      load();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const handleEdit = (e, d) => {
    e.stopPropagation();
    setEditing(d);
    setShowForm(true);
  };

  const handleRowClick = (d) => {
    if (d.servico_id) {
      // Navega para a lista de serviços com âncora para auto-expandir
      navigate(`/financeiro/servicos?svc=${d.servico_id}`);
    }
  };

  const total = list.reduce((s, e) => s + (e.valor || 0), 0);

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return <Navigate to="/" replace />;

  return (
    <div className="space-y-5" data-testid="financeiro-despesas">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Despesas</h1>
          <p className="text-sm text-muted-foreground">
            Saídas do módulo Financeiro · clique em uma linha vinculada para ir ao serviço
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="btn-nova-despesa">
          <Plus className="w-4 h-4 mr-2" /> Nova Despesa
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">De</Label><Input type="date" value={filters.data_inicio} onChange={(e) => setFilters(f => ({ ...f, data_inicio: e.target.value }))} /></div>
          <div><Label className="text-xs">Até</Label><Input type="date" value={filters.data_fim} onChange={(e) => setFilters(f => ({ ...f, data_fim: e.target.value }))} /></div>
          <div><Label className="text-xs">Categoria</Label>
            <Select value={filters.categoria_id || 'todas'} onValueChange={(v) => setFilters(f => ({ ...f, categoria_id: v === 'todas' ? '' : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end justify-end">
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Total no filtro</p>
              <p className="text-xl font-bold text-rose-600">{fmt(total)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando...</div>
      ) : list.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <TrendingDown className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Nenhuma despesa registrada.
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase">
              <tr>
                <th className="text-left p-3 w-28">Data</th>
                <th className="text-left p-3">Categoria</th>
                <th className="text-left p-3">Serviço</th>
                <th className="text-left p-3">Observação</th>
                <th className="text-right p-3 w-32">Valor</th>
                <th className="text-center p-3 w-24">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map(d => {
                const vinculada = !!d.servico_id;
                return (
                  <tr
                    key={d.id}
                    className={`border-t ${vinculada ? 'cursor-pointer hover:bg-muted/40 transition-colors' : ''}`}
                    onClick={() => handleRowClick(d)}
                    data-testid={`despesa-${d.id}`}
                  >
                    <td className="p-3 text-muted-foreground">{fmtDate(d.data)}</td>
                    <td className="p-3">
                      {d.categoria_nome ? (
                        <span
                          className="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ background: `${d.categoria_cor}22`, color: d.categoria_cor }}
                          data-testid={`categoria-${d.id}`}
                        >
                          {d.categoria_nome}
                        </span>
                      ) : <span className="text-muted-foreground text-xs italic">sem categoria</span>}
                    </td>
                    <td className="p-3">
                      {vinculada ? (
                        <span className="inline-flex items-center gap-1.5 text-cyan-700 hover:underline" data-testid={`servico-link-${d.id}`}>
                          <Link2 className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[200px]">{d.servico_titulo || 'Serviço'}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground text-xs max-w-xs truncate">
                      {d.observacoes || '—'}
                    </td>
                    <td className="p-3 text-right font-semibold text-rose-600">{fmt(d.valor)}</td>
                    <td className="p-3 text-center whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={(e) => handleEdit(e, d)} data-testid={`btn-edit-desp-${d.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => handleDelete(e, d.id)} data-testid={`btn-del-desp-${d.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {showForm && (
        <ExpenseFormModal
          editing={editing}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}
